import { trackGoogleAdsConversion } from 'analytics';
import forge from 'apis/forge';
import HomeBar from 'components/Home/HomeBar';
import { AuthUser } from 'dragonmouse-shared/interfaces';
import { useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { setAuthUser } from 'redux/mode/actions';
import { RootReducer } from 'redux/reducers';
import { hasEarlyAccess, mixpanelIdentify, mixpanelTrackEvent } from 'utils';
import * as S from './styles';

interface InviteCodeProps {
	email: string;
}

const InviteCode = ({ email }: InviteCodeProps) => {
	const dispatch = useDispatch();

	const { authUser } = useSelector(
		(r: RootReducer) => ({
			authUser: r.mode.authUser,
		}),
		shallowEqual
	);

	const [inviteCode, setInviteCode] = useState('');
	const [isRedeeming, setIsRedeeming] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// extract invite code from URL query parameter on component mount
	useEffect(() => {
		const urlParams = new URLSearchParams(window.location.search);
		const codeFromUrl = urlParams.get('code');
		if (codeFromUrl) {
			setInviteCode(codeFromUrl);
		}
	}, []);

	// force a reload when permissions are updated
	useEffect(() => {
		if (hasEarlyAccess(authUser)) {
			window.location.reload();
		}
	}, [authUser?.permissions]);

	const handleRedeemCode = async () => {
		const code = inviteCode.trim();
		if (!code) {
			return;
		}

		setIsRedeeming(true);
		setError(null);

		try {
			await forge.redeemInviteCode({ code });

			// Reload user to get updated permissions
			const updatedAuthUser = await forge.getAuthUser();
			mixpanelTrackEvent('Invite Code Redeemed', { code });
			mixpanelIdentify({ $invite_codes: [code] });
			if (hasEarlyAccess(updatedAuthUser)) {
				// go ahead and initialize the user (because the moment when the app tries to initialize the user may have passed, and duplicate initializations don't have a side effect)
				const { userCreated } = await forge.initializeUser();
				if (userCreated) {
					console.log('*- invite code -*');
					trackGoogleAdsConversion();
				}
			}
			// i think it's best to set the auth user after initializing the user
			// (since changing authUser currently causes App to reload, and we want to make sure to
			//  track the conversion before reloading)
			dispatch(setAuthUser(updatedAuthUser as AuthUser));
		} catch (err: any) {
			setError(err.response?.data?.error || 'Failed to redeem invite code');
		} finally {
			setIsRedeeming(false);
		}
	};

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------
	return (
		<S.Base>
			<HomeBar />
			<S.Content>
				<S.LeftColumn>
					<S.Headline>You&rsquo;re on the list</S.Headline>
					<S.Body>
						We&rsquo;ve added <S.EmailAddress>{email}</S.EmailAddress> to our waitlist. We&rsquo;ll
						notify you as soon as spots open up.
					</S.Body>
					<S.Body>Got a code? Enter it below for instant access.</S.Body>
					<S.InviteCodeContainer>
						<S.InviteCodeInput
							value={inviteCode}
							onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInviteCode(e.target.value)}
							onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
								if (e.key === 'Enter' && !isRedeeming && inviteCode.trim()) {
									handleRedeemCode();
								}
							}}
							placeholder="Enter invite code"
							disabled={isRedeeming}
						/>
						<S.Button onClick={handleRedeemCode} disabled={isRedeeming || !inviteCode.trim()}>
							{isRedeeming ? 'Submitting...' : 'Submit'}
						</S.Button>
					</S.InviteCodeContainer>
					<S.ErrorMessage>{error || ' '}</S.ErrorMessage>
					<S.Body>
						Really need access now? Email{' '}
						<S.EmailLink href="mailto:hello@moonchild.ai">hello@moonchild.ai</S.EmailLink> and tell
						us why. We read every message.
					</S.Body>
				</S.LeftColumn>
				<S.RightColumn>
					<img
						src="/images/skating-cat.png"
						alt="An illustrated cat riding a skateboard with a moon in the background"
					/>
				</S.RightColumn>
			</S.Content>
		</S.Base>
	);
};

export default InviteCode;
